/* eslint-disable no-case-declarations */
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import { Spinner } from 'reactstrap';
import Iframe from 'react-iframe';
import { useTranslation } from 'react-i18next';

import useApi from 'hooks/useApi';
import { setEmailAccount } from 'redux/actions/emailActions';

import BackgroundBox from 'shared/components/BackgroundBox';
import { LabelIcon } from 'shared/components/Labels';
import { Button } from 'shared/components/Buttons';
import { colors, fonts, fontSizes } from 'shared/defaultTheme';

import { ReactComponent as RefreshIcon } from 'assets/icon-rotate.svg';
import { ReactComponent as MailIcon } from 'assets/icon-mail.svg';
import { ReactComponent as CalendarCheckIcon } from 'assets/icon-calendar-check.svg';

const MailSectionCard = () => {
  const { accountApi, lexonApi } = useApi();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { entradaUsuario } = useSelector((state) => state.auth);
  const { emailAccount } = useSelector((state) => state.email);
  const [emailUrl, setEmailUrl] = useState('');
  const theme = useSelector((state) => state.theme);
  const { mainColor } = theme;
  const [loading, setLoading] = useState(false);

  const [, getAccountsDefault] = accountApi(
    {
      url: `accounts/auth/usermail/${entradaUsuario}/account/default`,
      method: 'GET',
    },
    { manual: true }
  );

  const [{ loading: tokenLexonLoading }, getTokenLexon] = lexonApi(
    {
      method: 'PUT',
    },
    { manual: true }
  );

  const getToken = async () => {
    const {
      data: { data },
    } = await getTokenLexon({
      data: {
        idClienteNavision: entradaUsuario,
      },
      url: `utilsauth/Lexon/token/lexon?addTerminatorToToken=true`,
    });
    return data;
  };

  const loadAccount = async () => {
    try {
      setLoading(true);
      const {
        data: { data },
      } = await getAccountsDefault();
      if (data) {
        const emailConfig = data;
        dispatch(setEmailAccount(emailConfig));
        const dataToken = await getToken();
        const token = dataToken.token.replace('/', '');
        switch (emailConfig.provider) {
          case 'GOOGLE':
            setEmailUrl(
              `${window.PATH_CL_GOO}/access/${dataToken.token}?prov=GO0&email=${
                emailConfig.account ? emailConfig.account : emailConfig.email
              }&appConnectMail=true`
            );
            break;
          case 'OUTLOOK':
            setEmailUrl(`${window.PATH_CL_OUT}/?appConnectMail=${token}`);
            break;
          case 'IMAP':
            setEmailUrl(
              `${window.PATH_CL_IMAP}/?appConnectMail=${token}&account=${emailConfig.email}`
            );
            break;
          default:
            break;
        }
      }
    } catch (error) {
      console.error('error load account:', error);
      dispatch(setEmailAccount(null));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAccount();
  }, []);

  const onConfigAccount = async () => {
    try {
      setLoading(true);
      const data = await getToken();
      window.open(`${window.PATH_CL_POR}/access/${data.token}/`, '_blank');
    } catch (error) {
      console.error('error lexon: ', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="pb-3 w-100">
      <EmailBackground className="ml-background-dashboard background-box__flex-home-mail">
        <div
          onClick={onConfigAccount}
          onKeyDown={onConfigAccount}
          role="button"
          tabIndex="0"
          className="d-flex home__mb-notifi mb-n1"
        >
          <LabelIcon
            label={t('home_email_section_label')}
            Icon={() => <MailIcon className="label-icon__notification" fill={colors.white} />}
            color={mainColor}
            className="ml-1 label-icon text-uppercase"
            classNameMain=""
          />
        </div>
        {loading ? (
          <div className="d-flex justify-content-center align-items-center">
            <Spinner />
          </div>
        ) : (
          <div />
        )}
        {emailAccount ? (
          <div className="mt-3">
            <Iframe
              url={emailUrl}
              width="100%"
              height="800px"
              id="emailIframe"
              frameBorder="0"
              className="frame-email"
              allow="fullscreen"
              display="initial"
              position="relative"
            />
          </div>
        ) : (
          <>
            {!tokenLexonLoading ? (
              <div className="d-flex justify-content-center">
                <div className="box-calendar-sync">
                  <div className="d-flex align-items-start mb-3">
                    <CalendarCheckIcon width={20} />
                    <span className="ml-2 title-calendars-sync">
                      {t('calendar_calendars_sync')}
                    </span>
                  </div>
                  <span className="info-account">{t('calendar_no_account')}</span>
                  <div className="btn-calendar-sync mt-3 mb-3">
                    <Button
                      className="text-uppercase"
                      type="button"
                      label={t('calendar_config_account')}
                      color="primary"
                      onClick={onConfigAccount}
                    />
                  </div>
                  <div className="d-flex justify-content-center">
                    {loading ? (
                      <Spinner />
                    ) : (
                      <>
                        <RefreshIcon width={15} height={20} />
                        <span className="refresh-calendars-sync ml-2">
                          {t('calendar_refresh_screen')}
                        </span>
                      </>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div className="d-flex justify-content-center align-items-center">
                <Spinner />
              </div>
            )}
          </>
        )}
      </EmailBackground>
    </div>
  );
};

const EmailBackground = styled(BackgroundBox)`
  #schedule_dialog_wrapper {
    min-height: 500px;
  }
  width: 90%;
  .box-calendar-sync {
    border: 1px solid black;
    height: auto;
    width: 70%;
    padding: 17px;
    .title-calendars-sync {
      font-family: ${fonts.default};
      font-weight: bold;
      font-size: ${`${fontSizes.md}px`};
    }
    .info-account {
      font-family: ${fonts.default};
    }
    .btn-calendar-sync {
      display: flex;
      justify-content: center;
      button {
        height: 42px;
        width: 181px;
      }
    }
    .refresh-calendars-sync {
      font-family: ${fonts.default};
      font-weight: bold;
      font-size: ${`${fontSizes.sm}px`};
    }
  }
`;

export default MailSectionCard;
